import { useCallback, useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { LogIn } from 'lucide-react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { object, string } from 'zod';

import { useFeatureFlag } from '@/app/services/features';
import { AuthenticationActions } from '@/app/slices';
import { AuthenticationType } from '@/app/slices/authentication';
import { addToast } from '@/app/slices/toast';
import { LocaleDropdown } from '@/components';
import { clearAuthTokens } from '@/lib/tokenStorage';
import {
  getNucleusAccessErrorMessage,
  hasNucleusAccess,
  useCurrentUser,
} from '@/services/auth';
import { authFetch, authServiceUrl } from '@/services/auth/authService';

import { LoginFormData, LoginSchema } from './schema';
import { useLogin } from './useLogin';

import styles from './Login.module.css';

const envSchema = object({
  VITE_AUTHENTICATION_TENANT_ID: string().min(1),
  VITE_AUTHENTICATION_APP_ID: string().min(1),
  VITE_AUTHENTICATION_REDIRECT_URI: string().min(1),
});

function getB2cLoginUrl(returnUrl: string) {
  const env = envSchema.parse(import.meta.env);
  const TENANT_ID = env.VITE_AUTHENTICATION_TENANT_ID;
  const AUTH_APP_ID = env.VITE_AUTHENTICATION_APP_ID;
  const REDIRECT_URI = env.VITE_AUTHENTICATION_REDIRECT_URI;

  const AUTHORITY = `https://login.microsoftonline.com/${TENANT_ID}`;

  const STATE = JSON.stringify({ returnUrl, loginUrl: location.href });

  const url = new URL(`${AUTHORITY}/oauth2/v2.0/authorize`);
  url.searchParams.set('client_id', AUTH_APP_ID);
  url.searchParams.set('redirect_uri', REDIRECT_URI);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('response_mode', 'form_post');
  url.searchParams.set('scope', ['profile', 'openid'].join(' '));
  url.searchParams.set('state', STATE);

  return url.toString();
}

function getAuthServiceLoginUrl(returnUrl: string) {
  const url = authServiceUrl('/api/auth/login');
  url.searchParams.set('returnUrl', returnUrl);
  return url.toString();
}

function getLoginUrl(returnUrl: string, useAuthService: boolean) {
  return useAuthService
    ? getAuthServiceLoginUrl(returnUrl)
    : getB2cLoginUrl(returnUrl);
}

function B2cLogin() {
  const { t } = useTranslation();
  const authServiceLoginEnabled = useFeatureFlag('nucleus_auth_service_login');
  const loginUrl = useMemo(() => {
    const callbackUrl = `${window.location.origin}/auth-callback`;
    return getLoginUrl(callbackUrl, authServiceLoginEnabled);
  }, [authServiceLoginEnabled]);

  const dispatch = useDispatch();
  const { me } = useSelector(
    (state: { authentication: AuthenticationType }) => state.authentication,
  );

  const { data: currentUser, isLoading, isFetching } = useCurrentUser();
  const [showSignup, setShowSignup] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  useEffect(() => {
    if (me?.adUserId || !currentUser) {
      return;
    }

    if (!hasNucleusAccess(currentUser)) {
      const errorMessage = getNucleusAccessErrorMessage(currentUser);
      console.error(
        'User does not have permission to access Nucleus:',
        errorMessage,
      );
      clearAuthTokens();
      dispatch(
        addToast({
          severity: 'error',
          summary: t('auth.accessDenied'),
          detail: errorMessage,
          sticky: true,
        }),
      );
      return;
    }

    dispatch({
      type: AuthenticationActions.UPDATE_ME,
      me: {
        adUserId: currentUser.azureObjectId,
        userId: currentUser.userId,
        email: currentUser.email,
        displayName: currentUser.displayName,
        givenName: currentUser.givenName,
        surname: currentUser.surname,
        jobTitle: currentUser.jobTitle,
        department: currentUser.department,
        companyName: currentUser.companyName,
        mobilePhone: currentUser.mobilePhone,
        preferredLanguage: currentUser.preferredLanguage,
        accountEnabled: currentUser.accountEnabled,
        groups: currentUser.roles?.map((r) => r.roleId) || [],
        permissions: currentUser.permissions || [],
        sites: currentUser.sites || [],
      },
    });
    dispatch({
      type: AuthenticationActions.UPDATE_IS_CSR,
      payload: currentUser.roles?.some((role) =>
        role.roleId.toLowerCase().includes('csr'),
      ),
    });
  }, [dispatch, me?.adUserId, currentUser]);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!showSignup || companies.length > 0) return;

      setCompaniesLoading(true);
      try {
        const res = await authFetch(
          '/api/companies?isActive=true&pageSize=100',
        );
        if (res.ok) {
          const data = await res.json();
          setCompanies(data.items || []);
        }
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      } finally {
        setCompaniesLoading(false);
      }
    };

    fetchCompanies();
  }, [showSignup, companies.length]);

  const handleSignupSubmit = useCallback(async () => {
    if (!signupEmail || signupLoading) return;

    const emailLower = signupEmail.toLowerCase().trim();

    if (!selectedCompany) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('auth.companyRequired'),
          detail: t('auth.selectCompany'),
        }),
      );
      return;
    }

    setSignupLoading(true);

    try {
      const checkRes = await authFetch(
        `/api/b2cmanagement/by-email/${encodeURIComponent(emailLower)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (checkRes.ok) {
        dispatch(
          addToast({
            severity: 'info',
            summary: t('auth.accountExists'),
            detail: t('auth.accountExistsDetail'),
            life: 8000,
          }),
        );
        setShowSignup(false);
        setSignupEmail('');
        setSelectedCompany(null);
        return;
      }

      if (checkRes.status !== 404) {
        throw new Error('Failed to verify account status. Please try again.');
      }

      const redirectUrl = `${window.location.origin}/login`;

      const invitePayload = {
        email: emailLower,
        sendInvitationMessage: true,
        redirectUrl: redirectUrl,
        companyId: selectedCompany.companyId,
        roleIds: [],
      };

      const res = await authFetch('/api/b2cmanagement/invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(invitePayload),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to send invitation');
      }

      await res.json();

      dispatch(
        addToast({
          severity: 'success',
          summary: t('auth.invitationSent'),
          detail: t('auth.invitationSentDetail', { email: emailLower }),
          life: 20000,
          sticky: false,
        }),
      );

      setShowSignup(false);
      setSignupEmail('');
      setSelectedCompany(null);
    } catch (error: any) {
      console.error('Signup error:', error);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('auth.signUpFailed'),
          detail: error.message || t('auth.signUpFailedDetail'),
          life: 10000,
        }),
      );
    } finally {
      setSignupLoading(false);
    }
  }, [signupEmail, signupLoading, selectedCompany, dispatch]);

  return (
    <div className={styles['login']}>
      <div className={`label-input-group ${styles['language-dropdown']}`}>
        <LocaleDropdown placeholder={t('auth.language')} />
      </div>

      {!showSignup ? (
        <>
          <a
            role="link"
            aria-disabled={isLoading || isFetching}
            href={isLoading || isFetching ? undefined : loginUrl}
            className="flex items-center justify-center rounded border-2 border-[#5e81ac] bg-[#5e81ac] p-2 font-semibold text-white no-underline hover:bg-[#81a1c1] aria-disabled:pointer-events-none aria-disabled:opacity-50"
          >
            <LogIn /> {t('auth.signIn')}
          </a>

          <button
            type="button"
            onClick={() => setShowSignup(true)}
            className="mt-4 text-sm text-[#5e81ac] hover:text-[#81a1c1] hover:underline"
          >
            {t('auth.noAccountSignUp')}
          </button>
        </>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          <p className="text-sm text-gray-600">
            {t('auth.signupInstructions')}
          </p>
          <Dropdown
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.value)}
            options={companies}
            optionLabel="name"
            placeholder={t('auth.selectYourCompany')}
            disabled={signupLoading || companiesLoading}
            className="w-full"
            filter
          />
          <InputText
            value={signupEmail}
            onChange={(e) => setSignupEmail(e.target.value)}
            placeholder={t('auth.emailPlaceholder')}
            type="email"
            disabled={signupLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSignupSubmit();
              }
            }}
          />
          <div className="flex gap-2">
            <Button
              label={t('auth.sendInvitation')}
              onClick={handleSignupSubmit}
              loading={signupLoading}
              disabled={!signupEmail || !selectedCompany}
              className="flex-1"
            />
            <Button
              label={t('common.cancel')}
              onClick={() => {
                setShowSignup(false);
                setSignupEmail('');
                setSelectedCompany(null);
              }}
              disabled={signupLoading}
              outlined
            />
          </div>
        </div>
      )}
    </div>
  );
}

function LegacyLogin() {
  const { t } = useTranslation();
  const { handleLogin, isLoading } = useLogin();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = useCallback(
    (data: LoginFormData) => {
      handleLogin(data.username, data.password);
    },
    [handleLogin],
  );

  return (
    <form className={styles['login']} onSubmit={handleSubmit(onSubmit)}>
      <div className={`label-input-group ${styles['language-dropdown']}`}>
        <LocaleDropdown placeholder={t('auth.language')} disabled={isLoading} />
      </div>
      <div className="label-input-group">
        <label>{t('auth.username')}</label>
        <Controller
          name="username"
          control={control}
          render={({ field, fieldState }) => (
            <div className="p-inputgroup flex-1">
              <span className="p-inputgroup-addon">
                <i className="pi pi-user"></i>
              </span>
              <InputText
                id={field.name}
                {...field}
                autoFocus
                autoComplete="username"
                className={classNames({ 'p-invalid': fieldState.invalid })}
                disabled={isLoading}
              />
            </div>
          )}
        />
        {errors.username && (
          <small className="p-error">{errors.username.message}</small>
        )}
      </div>
      <div className="label-input-group">
        <label>{t('auth.password')}</label>
        <Controller
          name="password"
          control={control}
          render={({ field, fieldState }) => (
            <div className="p-inputgroup flex-1">
              <span className="p-inputgroup-addon">
                <i className="pi pi-key"></i>
              </span>
              <InputText
                id={field.name}
                {...field}
                type="password"
                autoComplete="current-password"
                className={classNames({ 'p-invalid': fieldState.invalid })}
                disabled={isLoading}
              />
            </div>
          )}
        />
        {errors.password && (
          <small className="p-error">{errors.password.message}</small>
        )}
      </div>
      <Button
        className="login-button"
        label={t('auth.signIn')}
        type="submit"
        icon="pi pi-sign-in"
        loading={isLoading}
      />
    </form>
  );
}

export function Login() {
  const [params] = useSearchParams();
  const enableB2cLogin =
    import.meta.env.VITE_B2C === 'true' || params.get('b2c') === 'true';

  return enableB2cLogin ? <B2cLogin /> : <LegacyLogin />;
}
